import whaleNameData from './whales/whale-names.json' with { type: 'json' };

/**
 * Names the community uses for individual whales ("T137A" is "Jack", "J35" is
 * "Tahlequah"), shown next to the ID wherever a report names one.
 *
 * Seeded from the Orca Behavior Institute and Orca Conservancy by
 * scripts/seed-whale-names.ts, then grown one user-confirmed name at a time
 * through /orcatext. There is no complete public list of Bigg's nicknames, so
 * most T-codes have no name here; that's expected, and they show as the bare ID.
 */
export interface WhaleName {
  /** Normalized, see `normalizeWhaleId`. */
  id: string;
  name: string;
  sex?: 'M' | 'F';
  born?: number;
  died?: number;
  /** Normalized ID of the mother, when the source gives one. */
  mother?: string;
  /** Page the name came from, or "Orca Network text <date>" for ones learned from texts. */
  source: string;
}

export const WHALE_NAMES: WhaleName[] = whaleNameData as WhaleName[];

const BY_ID = new Map(WHALE_NAMES.map((w) => [w.id, w]));

/**
 * Uppercase, with catalogue zero-padding removed from each number ("T002C3" is
 * "T2C3", the way reports write it). A trailing "s" (a whole family, "T137s")
 * stays lowercase. Anything that isn't a whale ID comes back unchanged.
 */
export function normalizeWhaleId(id: string): string {
  const m = id.trim().match(/^([TJKL])(\d+(?:[A-Z]\d*)*?)(s?)$/i);
  if (!m) return id;
  const body = m[2]!.toUpperCase().replace(/\d+/g, (n) => String(Number(n)));
  return `${m[1]!.toUpperCase()}${body}${m[3] ? 's' : ''}`;
}

/** The name of one individual whale, or null. A family ("T137s") has no name of its own. */
export function whaleName(id: string): WhaleName | null {
  return BY_ID.get(normalizeWhaleId(id)) ?? null;
}

/**
 * A report's pod/whale ID, with its name when one is known:
 * "T137A" -> "T137A Jack", "T137s" -> "T137s (Loon's family)",
 * "J pod" or an unnamed ID -> unchanged.
 */
export function labelPod(pod: string): string {
  const id = normalizeWhaleId(pod);
  if (id.endsWith('s')) {
    const matriarch = BY_ID.get(id.slice(0, -1));
    return matriarch ? `${pod} (${matriarch.name}'s family)` : pod;
  }
  const whale = BY_ID.get(id);
  return whale ? `${pod} ${whale.name}` : pod;
}
